import { ref } from 'vue';
import { defineStore } from 'pinia';
import { ElMessage } from 'element-plus';

import { userLoginByCode, getUserMenuList, userLogout, getShopList } from '@/api/user';
import { buildAdminRoutes } from '@/utils/common';
import { STORAGE_KEYS } from '@/utils/constants/storage';
import { injectExtraMenus } from '@/utils/constants/menuExtras';
import Layout from '@/views/admin/layout/layout.vue';

import router from '@/router';

export const useUserStore = defineStore('user', () => {
    // ======================== State ========================
    const token = ref(window.localStorage.getItem(STORAGE_KEYS.TOKEN) || '');
    const userInfo = ref(
        window.localStorage.getItem(STORAGE_KEYS.USER_INFO)
            ? JSON.parse(window.localStorage.getItem(STORAGE_KEYS.USER_INFO) as string)
            : null
    );
    const menuList = ref(
        window.localStorage.getItem(STORAGE_KEYS.MENU_LIST)
            ? injectExtraMenus(JSON.parse(window.localStorage.getItem(STORAGE_KEYS.MENU_LIST) as string))
            : []
    );
    const home = ref(
        window.localStorage.getItem(STORAGE_KEYS.HOME) ? window.localStorage.getItem(STORAGE_KEYS.HOME) : ''
    );
    const shops = ref(
        window.localStorage.getItem(STORAGE_KEYS.SHOPS)
            ? JSON.parse(window.localStorage.getItem(STORAGE_KEYS.SHOPS) as string)
            : []
    );
    const currentShopIds = ref(window.localStorage.getItem(STORAGE_KEYS.CURRENT_SHOP_IDS) || '');

    // ======================== Actions ========================

    /**
     * 登录
     * @param mobileNo 手机号
     * @param code 验证码
     * @param businessId 商家ID
     */
    async function login({ mobileNo, code, businessId }: { mobileNo: string; code: string; businessId: number }) {
        const { result } = await userLoginByCode({ mobileNo, code, businessId });
        await loginSuccess(result);
    }

    /**
     * 退出登录
     * @param callApi 是否调用退出接口
     */
    async function logout(callApi: boolean = true) {
        if (callApi) {
            await userLogout({});
        }

        window.localStorage.removeItem(STORAGE_KEYS.TOKEN);
        window.localStorage.removeItem(STORAGE_KEYS.USER_INFO);
        window.localStorage.removeItem(STORAGE_KEYS.MENU_LIST);
        window.localStorage.removeItem(STORAGE_KEYS.HOME);

        token.value = '';
        userInfo.value = null;
        menuList.value = [];
        home.value = '';
    }

    /**
     * 登录成功回调
     */
    async function loginSuccess(data: User.LoginByCode.Response) {
        token.value = data.token;
        userInfo.value = data;

        window.localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
        window.localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(data));

        // 并行获取权限菜单和店铺列表（无依赖关系，减少登录等待时间）
        // 店铺列表获取失败（接口异常或功能点未授权返回非 1 状态）不阻断登录，仅置空店铺列表
        const [{ result: { home: _home, menuList: _menuList } }, shopListRes] = await Promise.all([
            getUserMenuList(),
            getShopList().catch(() => null)
        ]);
        const _shops = (shopListRes as any)?.state * 1 === 1 ? (shopListRes as any).result || [] : [];

        injectExtraMenus(_menuList);

        menuList.value = JSON.parse(JSON.stringify(_menuList));
        home.value = _home;
        window.localStorage.setItem(STORAGE_KEYS.MENU_LIST, JSON.stringify(_menuList));
        window.localStorage.setItem(STORAGE_KEYS.HOME, _home);

        shops.value = _shops;
        if (!window.localStorage.getItem(STORAGE_KEYS.CURRENT_SHOP_IDS) && _shops?.length) {
            window.localStorage.setItem(STORAGE_KEYS.CURRENT_SHOP_IDS, _shops[0].id);
        }
        window.localStorage.setItem(STORAGE_KEYS.SHOPS, JSON.stringify(_shops));

        const menus = _menuList.map((item: any) => item.menuList).flat();
        const routes = buildAdminRoutes(menus || []);

        // 登录注册路由（Layout 使用同步导入，避免首次进入 admin 时因懒加载产生白屏）
        router.addRoute({
            path: '/admin',
            name: 'admin',
            component: Layout,
            redirect: _home,
            children: routes
        });

        router.push({ path: '/admin' });

        // 登录成功后在浏览器空闲时段预加载高频页面组件，减少首次点击菜单的等待
        preloadHighFrequencyPages();
    }

    /**
     * 预加载高频访问页面组件（利用浏览器空闲时段，减少首次点击菜单的等待时间）
     */
    function preloadHighFrequencyPages() {
        // requestIdleCallback：在浏览器空闲时执行，不阻塞主线程和首屏渲染
        const idlePreload = window.requestIdleCallback || window.setTimeout;
        idlePreload(() => {
            // 首页看板
            import('@/views/admin/home/overview.vue');
            // 产品列表（核心高频页面）
            import('@/views/admin/product/list.vue');
            // 订单列表（核心高频页面）
            import('@/views/admin/order/list.vue');
        });
    }

    /**
     * 设置当前选中的店铺
     */
    function setCurrentShops(ids: string) {
        currentShopIds.value = ids;
        window.localStorage.setItem(STORAGE_KEYS.CURRENT_SHOP_IDS, ids);
    }

    /**
     * 手动刷新权限菜单（重新获取 getUserMenuList 并重建路由）
     */
    async function refreshMenus() {
        const { result } = await getUserMenuList();
        const _home = result?.home || home.value;
        const _menuList = result?.menuList || [];

        if (!_menuList || _menuList.length === 0) {
            ElMessage.warning('未获取到菜单数据，请重新登录');
            return;
        }

        injectExtraMenus(_menuList);

        menuList.value = JSON.parse(JSON.stringify(_menuList));
        home.value = _home;
        window.localStorage.setItem(STORAGE_KEYS.MENU_LIST, JSON.stringify(_menuList));
        window.localStorage.setItem(STORAGE_KEYS.HOME, _home);

        // 重新注册 admin 路由
        if (router.hasRoute('admin')) {
            router.removeRoute('admin');
        }
        const menus = _menuList.map((item: any) => item.menuList).flat();
        const routes = buildAdminRoutes(menus || []);
        router.addRoute({
            path: '/admin',
            name: 'admin',
            component: Layout,
            redirect: _home,
            children: routes
        });

        ElMessage.success('权限刷新成功，即将重新加载页面');
        // 重新加载以应用最新路由配置（route.meta 不会自动响应式更新）
        window.location.reload();
    }

    /**
     * 扫码登录换取会话后，复用同一装配流程（菜单 / 店铺 / 动态路由），避免二次实现导致口径分裂。
     *
     * @param data 会话对象（`/scanLogin/exchange.json` 与 `/login.json` 同口径：PageSession）
     */
    async function applyLoginSession(data: User.LoginByCode.Response) {
        await loginSuccess(data);
    }

    return {
        token,
        userInfo,
        menuList,
        home,
        shops,
        currentShopIds,
        login,
        logout,
        loginSuccess,
        applyLoginSession,
        setCurrentShops,
        refreshMenus
    };
});
